// src/components/public/ReportarPagoModal.js
import React, { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button, Input, Label, Checkbox } from '../ui'
import { Upload, AlertCircle, CheckCircle2 } from 'lucide-react'
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard'

const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']
const MAX_SIZE = 5 * 1024 * 1024

const formatMoney = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`

const ReportarPagoModal = ({ open, onOpenChange, token, pin, cargoInicial, jugadorActual, otrosCargosJugador = [], otrosCargosFamilia = [], replaces = null, prefill = null, onSuccess }) => {
  const [step, setStep] = useState(1)
  const guard = useUnsavedGuard({ onClose: () => { if (step === 4) onSuccess?.(); onOpenChange(false) } })
  const [selectedCargos, setSelectedCargos] = useState({}) // { cargoId: monto }
  const [showOtros, setShowOtros] = useState(false)
  const [showHermanos, setShowHermanos] = useState(false)
  const [form, setForm] = useState({
    fecha_pago: new Date().toISOString().slice(0, 10),
    metodo_pago: 'transferencia',
    referencia: '',
    notas: '',
    monto_total: '',
  })
  const [file, setFile] = useState(null)
  const [filePreview, setFilePreview] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // Init cuando se abre
  useEffect(() => {
    if (!open) return
    if (prefill) {
      // correccion: pre-llenar con datos de la rechazada
      setSelectedCargos(prefill.cargos || {})
      setForm({
        fecha_pago: prefill.fecha_pago || new Date().toISOString().slice(0, 10),
        metodo_pago: prefill.metodo_pago || 'transferencia',
        referencia: prefill.referencia || '',
        notas: prefill.notas || '',
        monto_total: String(prefill.monto_total || ''),
      })
    } else if (cargoInicial) {
      setSelectedCargos({ [cargoInicial.id]: String(cargoInicial.saldo) })
      setForm((f) => ({ ...f, monto_total: String(cargoInicial.saldo) }))
    }
    setStep(1)
    setFile(null)
    setFilePreview(null)
    setError(null)
    guard.resetDirty()
  }, [open, cargoInicial, prefill]) // eslint-disable-line react-hooks/exhaustive-deps

  const totalCargos = useMemo(
    () => Object.values(selectedCargos).reduce((s, v) => s + (parseFloat(v) || 0), 0),
    [selectedCargos]
  )

  // Mientras estes seleccionando cargos (paso 1), el monto_total
  // se sincroniza con la suma. Al avanzar a paso 2 lo puedes ajustar
  // si quieres reportar que pagaste de mas (el sobrante queda a favor).
  useEffect(() => {
    if (!open || step !== 1) return
    setForm((f) => ({ ...f, monto_total: totalCargos > 0 ? totalCargos.toFixed(2) : '' }))
  }, [totalCargos, open, step])

  const toggleCargo = (cargo, checked) => {
    guard.markDirty()
    setSelectedCargos((prev) => {
      const next = { ...prev }
      if (checked) next[cargo.id] = String(cargo.saldo)
      else delete next[cargo.id]
      return next
    })
  }

  const handleFileChange = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    setError(null)
    if (!ALLOWED.includes(f.type)) {
      setError('Tipo no permitido. Solo JPG, PNG, WebP o PDF.')
      return
    }
    if (f.size > MAX_SIZE) {
      setError('Archivo demasiado grande (max 5 MB).')
      return
    }
    setFile(f)
    if (f.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (ev) => setFilePreview(ev.target.result)
      reader.readAsDataURL(f)
    } else {
      setFilePreview(null)
    }
  }

  const canNext1 = Object.keys(selectedCargos).length > 0 && totalCargos > 0
  const canNext2 = form.fecha_pago && form.metodo_pago && parseFloat(form.monto_total) >= totalCargos
  const canSubmit = !!file && canNext2

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    const fd = new FormData()
    fd.append('token', token)
    fd.append('pin', pin)
    fd.append('monto_total', form.monto_total)
    fd.append('fecha_pago', form.fecha_pago)
    fd.append('metodo_pago', form.metodo_pago)
    if (form.referencia) fd.append('referencia', form.referencia)
    if (form.notas) fd.append('notas', form.notas)
    fd.append('cargos', JSON.stringify(
      Object.entries(selectedCargos).map(([id, m]) => ({
        cargo_id: id,
        monto_propuesto: parseFloat(m),
      }))
    ))
    if (replaces) fd.append('replaces_solicitud_id', replaces)
    fd.append('comprobante', file)

    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY
      const res = await fetch(`${supabaseUrl}/functions/v1/submit-payment-request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
        },
        body: fd,
      })
      const result = await res.json()
      if (!res.ok) {
        setError(result.message || result.error || 'Error al enviar')
        setSubmitting(false)
        return
      }
      setSubmitting(false)
      guard.resetDirty()
      setStep(4)
    } catch (e) {
      setSubmitting(false)
      setError('Error de red. Intenta de nuevo.')
    }
  }

  const handleClose = () => {
    guard.onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {step === 4
              ? 'Pago enviado'
              : `${prefill ? 'Corregir y reenviar' : 'Reportar pago'} — Paso ${step} de 3`}
          </DialogTitle>
        </DialogHeader>

        {/* PASO 1: Cargos */}
        {step === 1 && (
          <div className="space-y-3">
            <Label>Que cargos pagaste?</Label>
            {cargoInicial && (
              <div className="border rounded p-2 bg-amber-50">
                <Checkbox checked={!!selectedCargos[cargoInicial.id]} onCheckedChange={(c) => toggleCargo(cargoInicial, c)} />
                <span className="ml-2 text-sm">{cargoInicial.concepto} — {formatMoney(cargoInicial.saldo)}</span>
              </div>
            )}

            <button type="button" onClick={() => setShowOtros((v) => !v)} className="text-xs text-blue-600">
              {showOtros ? '−' : '+'} Pague mas conceptos en el mismo SPEI
            </button>
            {showOtros && (
              <div className="space-y-1 pl-2 border-l-2">
                {otrosCargosJugador.map((c) => (
                  <div key={c.id} className="flex items-center gap-2">
                    <Checkbox checked={!!selectedCargos[c.id]} onCheckedChange={(ck) => toggleCargo(c, ck)} disabled={!!c.solicitud_pendiente} />
                    <span className="text-sm">{c.concepto} — {formatMoney(c.saldo)}</span>
                    {c.solicitud_pendiente && <span className="text-[10px] text-amber-600">(en revision)</span>}
                  </div>
                ))}
              </div>
            )}

            {otrosCargosFamilia.length > 0 && (
              <>
                <button type="button" onClick={() => setShowHermanos((v) => !v)} className="text-xs text-blue-600">
                  {showHermanos ? '−' : '+'} Ver cargos de otros hijos
                </button>
                {showHermanos && (
                  <div className="space-y-1 pl-2 border-l-2">
                    {otrosCargosFamilia.map((c) => (
                      <div key={c.id} className="flex items-center gap-2">
                        <Checkbox checked={!!selectedCargos[c.id]} onCheckedChange={(ck) => toggleCargo(c, ck)} disabled={!!c.solicitud_pendiente} />
                        <span className="text-sm">
                          {c.jugador_nombre}: {c.concepto} — {formatMoney(c.saldo)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <p className="text-sm border-t pt-2">
              Total seleccionado: <strong>{formatMoney(totalCargos)}</strong>
            </p>
          </div>
        )}

        {/* PASO 2: Datos del pago */}
        {step === 2 && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="monto">Monto total que pagaste</Label>
              <Input id="monto" type="number" step="0.01" value={form.monto_total}
                onChange={(e) => setForm({ ...form, monto_total: e.target.value })} />
              <p className="text-[10px] text-muted-foreground mt-1">
                Suma de cargos seleccionados: <strong>{formatMoney(totalCargos)}</strong>.
                Si pagaste mas (ej. transferencia redondeada), ajusta arriba; el sobrante se queda como saldo a favor.
              </p>
              {parseFloat(form.monto_total) > totalCargos && (
                <p className="text-[10px] text-emerald-600 mt-1">
                  Sobrante {formatMoney(parseFloat(form.monto_total) - totalCargos)} se queda como saldo a favor.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="fecha">Fecha de pago</Label>
              <Input id="fecha" type="date" value={form.fecha_pago}
                onChange={(e) => setForm({ ...form, fecha_pago: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="metodo">Metodo</Label>
              <select id="metodo" value={form.metodo_pago}
                onChange={(e) => setForm({ ...form, metodo_pago: e.target.value })}
                className="w-full border rounded p-2 text-sm">
                <option value="transferencia">Transferencia</option>
                <option value="efectivo">Efectivo</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <div>
              <Label htmlFor="ref">Referencia (folio SPEI, opcional)</Label>
              <Input id="ref" value={form.referencia}
                onChange={(e) => setForm({ ...form, referencia: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="notas">Notas (opcional)</Label>
              <textarea id="notas" maxLength={500} className="w-full border rounded p-2 text-sm"
                value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
            </div>
          </div>
        )}

        {/* PASO 3: Comprobante */}
        {step === 3 && (
          <div className="space-y-3">
            <Label htmlFor="file">Sube el comprobante (foto del SPEI / ticket)</Label>
            <input id="file" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={handleFileChange} />
            <p className="text-[10px] text-muted-foreground">JPG, PNG, WebP o PDF. Max 5 MB.</p>
            {filePreview && <img src={filePreview} alt="preview" className="max-h-40 rounded border" />}
            {file && !filePreview && <p className="text-sm">{file.name}</p>}
          </div>
        )}

        {/* PASO 4: Confirmacion de exito */}
        {step === 4 && (
          <div className="py-6 text-center space-y-3">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <p className="text-base font-semibold">Tu pago se envio</p>
            <p className="text-sm text-muted-foreground">
              El admin del club lo revisara pronto. Los cargos seleccionados aparecen como
              <strong> "En revision"</strong> hasta que se aprueben.
            </p>
          </div>
        )}

        {step !== 4 && error && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="h-4 w-4" /> {error}
          </p>
        )}

        <DialogFooter>
          {step !== 4 && step > 1 && (
            <Button variant="outline" onClick={() => setStep(step - 1)}>Atras</Button>
          )}
          {step < 3 && (
            <Button onClick={() => setStep(step + 1)} disabled={step === 1 ? !canNext1 : !canNext2}>
              Continuar
            </Button>
          )}
          {step === 3 && (
            <Button onClick={handleSubmit} disabled={submitting || !canSubmit}>
              {submitting ? 'Enviando...' : (
                <><Upload className="h-4 w-4 mr-1" /> Enviar reporte</>
              )}
            </Button>
          )}
          {step === 4 && (
            <Button onClick={handleClose}>Cerrar</Button>
          )}
        </DialogFooter>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  )
}

export default ReportarPagoModal
